/**
 * Admin Ingredient Units Page
 *
 * Bulk editor for fixing "servings" misuse in RecipeIngredient.displayUnit
 * Uses TanStack Table with inline editing via shadcn Select component
 */

import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";

import { getIngredientUnitsToFix, bulkUpdateIngredientUnits } from "wasp/client/operations";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

// Common unit options for inline editing
const UNIT_OPTIONS = [
  { value: "tsp", label: "tsp (teaspoon)" },
  { value: "tbsp", label: "tbsp (tablespoon)" },
  { value: "cup", label: "cup" },
  { value: "cups", label: "cups" },
  { value: "oz", label: "oz (ounce)" },
  { value: "lb", label: "lb (pound)" },
  { value: "g", label: "g (gram)" },
  { value: "kg", label: "kg (kilogram)" },
  { value: "ml", label: "ml (milliliter)" },
  { value: "l", label: "l (liter)" },
  { value: "", label: "(no unit)" },
];

interface IngredientUnitRecord {
  id: string;
  recipeId: string;
  recipeTitle: string;
  ingredientName: string;
  quantity: number;
  currentUnit: string;
  notes: string;
  isSeasoning: boolean;
}

interface LocalEdit {
  id: string;
  newUnit: string;
}

type ColumnType = ColumnDef<IngredientUnitRecord, any>;

export default function AdminIngredientUnitsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Boolean((user as any)?.isAdmin);

  const [localEdits, setLocalEdits] = useState<Map<string, string>>(new Map());
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user && !isAdmin) navigate("/");
  }, [user, isAdmin]);

  const { data, isLoading, error, refetch } = useQuery(
    getIngredientUnitsToFix,
    undefined,
    { enabled: isAdmin }
  );

  const doBulkUpdate = useAction(bulkUpdateIngredientUnits);

  // Local edit handlers
  const handleUnitChange = (id: string, newUnit: string) => {
    setLocalEdits(prev => {
      const updated = new Map(prev);
      updated.set(id, newUnit);
      return updated;
    });
  };

  const handleSaveChanges = async () => {
    if (localEdits.size === 0) {
      alert("No changes to save");
      return;
    }

    setIsSaving(true);
    try {
      const updates = Array.from(localEdits.entries()).map(([id, newUnit]) => {
        const record = data?.find(r => r.id === id);
        return {
          id,
          oldUnit: record?.currentUnit || "servings",
          newUnit
        };
      });

      const result = await doBulkUpdate({ updates });

      if (result.success) {
        alert(`Successfully updated ${result.successCount} records!`);
        setLocalEdits(new Map());
        refetch();
      } else {
        alert(`Updated ${result.successCount} records, but ${result.errorCount} failed. Check console for details.`);
        console.error("Bulk update errors:", result.results.filter(r => !r.success));
      }
    } catch (error: any) {
      alert(`Failed to update: ${error.message}`);
      console.error("Bulk update error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetChanges = () => {
    setLocalEdits(new Map());
  };

  // Table columns
  const columnHelper = createColumnHelper<IngredientUnitRecord>();

  const columns = useMemo<ColumnType[]>(
    () => [
      columnHelper.accessor("recipeTitle", {
        header: "Recipe",
        cell: (info) => (
          <div className="max-w-xs truncate" title={info.getValue()}>
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("ingredientName", {
        header: "Ingredient",
        cell: (info) => (
          <div className="flex items-center gap-2">
            {info.getValue()}
            {info.row.original.isSeasoning && (
              <Badge variant="outline" className="text-xs">
                seasoning
              </Badge>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("quantity", {
        header: "Qty",
        cell: (info) => info.getValue().toFixed(2),
      }),
      columnHelper.accessor("currentUnit", {
        header: "Current Unit",
        cell: (info) => (
          <Badge variant="destructive">{info.getValue()}</Badge>
        ),
      }),
      columnHelper.display({
        id: "newUnit",
        header: "New Unit",
        cell: (info) => {
          const id = info.row.original.id;
          const currentEdit = localEdits.get(id) || "";

          return (
            <Select
              value={currentEdit}
              onValueChange={(value) => handleUnitChange(id, value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select unit..." />
              </SelectTrigger>
              <SelectContent>
                {UNIT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      }),
      columnHelper.accessor("notes", {
        header: "Notes",
        cell: (info) => (
          <div className="max-w-xs truncate text-sm text-gray-500" title={info.getValue()}>
            {info.getValue() || "-"}
          </div>
        ),
      }),
    ],
    [localEdits]
  );

  const table = useReactTable({
    data: data || [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (!isAdmin) return null;

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Ingredient Units Editor</h1>
        <p className="text-gray-600">
          Fix records where "servings" is incorrectly used as a unit. Typically seasonings should use tbsp/tsp.
        </p>
      </div>

      {/* Stats & Actions */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-6">
            <div>
              <div className="text-2xl font-bold">{data?.length || 0}</div>
              <div className="text-sm text-gray-500">Total Records</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{data?.filter(r => r.isSeasoning).length || 0}</div>
              <div className="text-sm text-gray-500">Likely Seasonings</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{localEdits.size}</div>
              <div className="text-sm text-gray-500">Pending Changes</div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleResetChanges}
              disabled={localEdits.size === 0 || isSaving}
            >
              Reset Changes
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={localEdits.size === 0 || isSaving}
            >
              {isSaving ? "Saving..." : `Save ${localEdits.size} Changes`}
            </Button>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <Input
              placeholder="Filter by recipe name..."
              value={(table.getColumn("recipeTitle")?.getFilterValue() as string) ?? ""}
              onChange={(e) =>
                table.getColumn("recipeTitle")?.setFilterValue(e.target.value)
              }
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="Filter by ingredient name..."
              value={(table.getColumn("ingredientName")?.getFilterValue() as string) ?? ""}
              onChange={(e) =>
                table.getColumn("ingredientName")?.setFilterValue(e.target.value)
              }
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      {isLoading ? (
        <Card className="p-8 text-center">
          <div className="text-gray-500">Loading records...</div>
        </Card>
      ) : error ? (
        <Card className="p-8 text-center">
          <div className="text-red-500">Error loading records: {error.message}</div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8 text-gray-500">
                    No records found. All units are clean! 🎉
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={localEdits.has(row.original.id) ? "bg-blue-50" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
